/**
 * The cms module handles pages
 *
 * @module cms
 * @todo document code!
 */

// import Netx from '@netx/core/bootstrap';
import {addMenus, Menus} from '@netx/core/legacyMenuManager';
import {
	ExtendLegacyAction,
	FindLegacyAction,
	RegisterLegacyAction,
} from '@netx/core/legacyActionManager';

// Models
import CMSActionModel from './models/CMSAction';
import CMSBlockModel from './models/CMSBlock';
import PageModel from './models/CMSPage';

// Collections
import CMSBlocksCollection from './collections/CMSBlocks';

import Menu from '@netx/core/lib/netx-menu';
import categoryCustomConfig from '@netx/core-category/config';

import routes from './routes';

import {appRouter, getPrefixedLogger} from '@netx/core-next/instance';
import {randomChars} from '@netx/core/utils/randomChars';
import {modalViews} from '@netx/core-app/singletons/modalViews';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';
import {currentPortalPages} from '@netx/core-cms/singletons/currentPortalPages';

const logger = getPrefixedLogger('cms');

// MODULE INITIALIZATION
// =====================
// All collection and model instances and event listeners
// are created when the module is initialized.

/**
 * Init handler
 * @event module:cms#cms:init
 */
dispatcher.once('cms:init', function () {
	const NS = (Netx.cms = Netx.cms || {});

	dispatcher.on('cms:route:category:root', function () {
		appRouter.navigate('category/' + categoryCustomConfig.categoryid, {
			trigger: true,
		});
	});

	/** Begin template option menu actions and listener generator */
	// This function creates on the fly events and dispatchers
	// Possible output:
	// 'cms:page:template:edit:1',
	// 'cms:page:template:edit:2'
	// ...
	/**
	 * @event module:cms#cms:init:templates
	 */
	dispatcher.on('cms:init:templates', function (templates) {
		var menuActions = [],
			actions = [];

		_.each(templates, function (template, idx) {
			var action = {
				event: 'cms:page:template:edit:' + idx,
				label: template,
				wrapper: function (view, e) {
					return [view.data];
				},
			};
			if (_.isUndefined(FindLegacyAction(action.event))) {
				RegisterLegacyAction(action);
			} else {
				ExtendLegacyAction(action);
			}
			dispatcher.off('cms:page:template:edit:' + idx);
			dispatcher.on('cms:page:template:edit:' + idx, function (model) {
				// Convert to a normal form-submit event.
				dispatcher.trigger(
					'cms:page:template:edit:confirmed',
					{template: idx},
					model,
				);
			});

			menuActions.push('cms:page:template:edit:' + idx);
		});

		RegisterLegacyAction(actions);
		addMenus({
			/**
			 * Template-choosing menu
			 */
			chooseTemplate: new Menu({
				label: i18n.t('i18n.chooseTemplate'),
				actions: menuActions,
			}),
		});
	});
	/** End template action and listen generator */

	/***************
	 * CMS Pages
	 **************/

	/**
	 * @event module:cms#cms:page:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on(
		'cms:page:show',
		function (viewOptions) {
			viewOptions = viewOptions || {};
			if (!viewOptions.isModal) {
				// TODO: we need a cms viewCollection to push non modal views into
			}
			import(/* webpackChunkName: "view-CMSPage" */ './views/CMSPageView').then(
				(View) => {
					modalViews.add({
						viewClass: View.default,
						showActions: ['cms:page:show'],
						viewOptions: viewOptions,
					});
				},
			);
		},
		window,
	);
	/**
	 * @event module:cms#cms:page:route
	 * @param {string} pageName Page name
	 * @fires module:cms#cms:page
	 */
	dispatcher.on('cms:page:route', function (pageName) {
		if (_.isUndefined(pageName)) {
			console.warn(
				'A page route was attempted but the page name was not provided.',
			);
			return;
		}
		dispatcher.trigger('cms:page', pageName);
	});
	/**
	 * @event module:cms#cms:page
	 * @param {string} pageName Page name
	 * @fires module:cms#cms:page:show
	 */
	dispatcher.on('cms:page', function (pageName) {
		var action, chapterName, pathArr, safeName, template, view, viewType;

		pathArr = pageName.split('/');
		pageName = pathArr[pathArr.length - 1];

		chapterName = pathArr.length > 1 ? pathArr[pathArr.length - 2] : null;

		action = FindLegacyAction('cms:page:' + pageName + ':route');
		safeName = pageName.replace(/\//g, '-');
		template = 'page-' + safeName;

		var pageModel = currentPortalPages.findWhere({slug: pageName});
		if (!pageModel) {
			console.debug(
				'%c cms:page:route - No page for route: %o ',
				'background: #0085FF; color: #FFF',
				pageName,
			);
		} else {
			// Show
			dispatcher.trigger(
				'cms:page:show',
				_.extend(
					{
						className: 'page ' + pageModel.get('template'),
						id: template,
						model: pageModel,
						collection: pageModel.get('blocks'),
						removeOnHide: true,
					},
					action && action.viewOpts ? action.viewOpts : {},
				),
			);

			// nested page paths, this was for intuit, likely unused now
			if (chapterName) {
				dispatcher.trigger('cms:subnav:update', chapterName);
			} else {
				dispatcher.trigger('cms:subnav:hide');
			}

			dispatcher.trigger('cms:page:routed', pageName);
			dispatcher.trigger('cms:page:created', pageModel);
		}
	});

	/**
	 * @event module:cms#cms:page:block:add
	 * @param {object} page CMSPage model
	 * @param {object} event click event
	 * @fires module:cms#cms:page:block:save:error
	 */
	dispatcher.on('cms:page:block:add', function (page, event) {
		if (!page) {
			console.warn('no page for block');
			return;
		}
		if (!page.get('blocks')) {
			page.set({blocks: new CMSBlocksCollection()});
		}
		// Vars
		var currentBlocks = page.get('blocks'),
			// it's simplest to just start with an html block, no colellections necessary
			blockType = 'html';
		const newBlock = new CMSBlockModel(
			{
				type: blockType,
				userId: userProfile.id,
				pageId: page.id,
				ordinal: currentBlocks.length + 1,
			},
			{parse: true},
		);

		// first save the block so it has an id.  that way we can sort it
		newBlock.save().then(
			(blockid) => {
				// then add it to our page blocks
				currentBlocks.add(newBlock);

				// save the page, blocks can be added quickly,
				// saving the page with each new block can cause issues
				// no promise necessary in this instance
				page.debouncedSave();
			},
			(err) => {
				console.error('block save error');
				dispatcher.trigger(
					'cms:page:block:save:error',
					i18n.t('i18n.warningDataSaveFail'),
				);
			},
		);
	});
	/**
	 * @event module:cms#cms:page:block:save:error
	 * @param {string} error Error string
	 * @param {Netx.Models.CMSBlock} block CMS block model
	 * @fires module:app#app:alert
	 */
	dispatcher.on(
		'cms:page:block:save:error',
		function (error) {
			dispatcher.trigger('app:alert', error, i18n.t('i18n.error'));
		},
		window,
	);

	/**
	 * @event module:cms#cms:page:block:delete
	 * @param {Netx.Models.CMSBlock} block CMS block model
	 * @fires module:cms#cms:page:block:delete:error
	 * @fires module:cms#cms:page:block:delete:success
	 */
	dispatcher.on('cms:page:block:delete', function (block) {
		block.destroy().then(
			function () {
				dispatcher.trigger('cms:page:block:delete:success', block);
			},
			function (err) {
				dispatcher.trigger('cms:page:block:delete:error', err, block);
			},
		);
	});
	/**
	 * @event module:cms#cms:page:block:delete:error
	 * @param {string} error Error string
	 * @param {Netx.Models.CMSBlock} block CMS block model
	 * @fires module:app#app:alert
	 */
	dispatcher.on(
		'cms:page:block:delete:error',
		function (error, block) {
			dispatcher.trigger('app:alert', error, i18n.t('i18n.error'));
		},
		window,
	);
	/**
	 * @event module:cms#cms:page:block:delete:success
	 * @param {Netx.Models.CMSBlock} block CMS block model
	 */
	dispatcher.on(
		'cms:page:block:delete:success',
		function (block) {
			dispatcher.trigger('notification', {
				module: 'cms',
				title: i18n.t('i18n.success'),
				description: i18n.t('i18n.deleteSuccess'),
				type: 'success',
			});
		},
		window,
	);

	dispatcher.on('cms:block:edit', function (block) {});

	/**
	 * Handle a single block edit
	 */
	dispatcher.on('cms:block:edit:confirmed', function (form, block, page) {
		var isNew = block.isNew();

		var mungedForm = {
			type: form.type,
			pageId: page.id,
			userId: userProfile.id,
		};

		//
		// Munge the form data.
		// (http://www.dictionary.com/browse/munge)
		//
		// TODO: rather than sanitize the object of its unused data here,
		// maybe wrap the object save method ?  I don't want to remove it
		// here because the user might switch back & forth between types &
		// we shouldn't destroy their state (yet).
		//

		switch (form.type) {
			case 'asset':
				mungedForm.contentIds = mungedForm.assetIds = [
					form['contentIds-asset'],
				];

				//todo: figure out why am I doing this when I did which currently is right now and then
				mungedForm.buttonDownload = form.buttonDownload;
				mungedForm.buttonCart = form.buttonCart;
				mungedForm.linkUrl = form.linkUrl;
				mungedForm.linkTitle = form.linkTitle;
				mungedForm.imageSize = form.imageSize;
				mungedForm.displayAssetName = form.displayAssetName;

				//mungedForm.html = '';
				break;
			case 'savedCart':
				mungedForm.contentIds = [form['contentIds-' + form.type]];
				mungedForm.listTitle = form.listTitle;
				mungedForm.listButtonDownload = true;
				mungedForm.listButtonCart = true;
				mungedForm.itemImageSize = form.itemImageSize;
				mungedForm.displayListItemName = '1';
				break;
			case 'categoryAssetList':
				mungedForm.contentIds = [form['contentIds-' + form.type]];
				mungedForm.listTitle = form.listTitle;
				mungedForm.listButtonDownload = form.listButtonDownload;
				mungedForm.listButtonCart = form.listButtonCart;
				mungedForm.itemImageSize = form.itemImageSize;
				mungedForm.displayListItemName = form.displayListItemName;
				break;
			case 'html':
				//mungedForm.contentIds = mungedForm.assetIds = [];
				mungedForm.html = form.html;
				break;
			default:
				mungedForm.contentIds = [form['contentIds-' + form.type]];
				//mungedForm.assetIds = [];
				// Do we use the html field here or not?
				mungedForm.html = form.html;
			//mungedForm.html = '';
		}
		mungedForm.contentLabels = [form.contentLabel];

		// set now honors 'parse' option -- see CMSBlock
		block.set(mungedForm, {parse: true});

		// dispatcher.trigger('cms:block:edit:success', form, block, page);

		block.save().then(
			() => {
				dispatcher.trigger('cms:block:edit:success', block, page);
			},
			(err) => {
				dispatcher.trigger('cms:block:edit:error', err, block);
			},
		);
	});
	/**
	 * @event module:cms#cms:block:edit:success
	 * @fires module:cms#cms:page:save:confirmed
	 */
	dispatcher.on('cms:block:edit:success', (block, page) => {
		// DAM-15436 - we have to save the page after saving the block
		// on the other end it come back from the goo
		dispatcher.trigger('cms:page:save:confirmed', {}, page);
	});

	/*********************
		CMS Actions
	*********************/

	/**
	 * @event module:cms#cms:action:edit:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on(
		'cms:action:edit:show',
		function (viewOptions) {
			import(
				/* webpackChunkName: "view-CMSActionEdit" */ './views/CMSActionEditView'
			).then((View) => {
				modalViews.add({
					viewClass: View.default,
					showActions: ['cms:action:edit:show'],
					viewOptions: viewOptions,
				});
			});
		},
		window,
	);

	/**
	 * @event module:cms#cms:action:new
	 * @fires module:cms#cms:action:edit:show
	 */
	dispatcher.on('cms:action:new', function (portalId) {
		// Show
		dispatcher.trigger('cms:action:edit:show', {
			model: new CMSActionModel(
				{
					portalId: portalId,
				},
				{
					collection: Netx.cms.portal.actions,
				},
			),
			pages: Netx.cms.portal.pages,
		});
	});
	/**
	 * @event module:cms#cms:action:edit
	 * @fires module:cms#cms:action:edit:show
	 */
	dispatcher.on('cms:action:edit', function (action) {
		// Show
		dispatcher.trigger('cms:action:edit:show', {
			model: action,
			pages: Netx.cms.portal.pages,
		});
	});

	/**
	 * @event module:cms#cms:action:edit:confirmed
	 * @fires module:cms#cms:action:edit:success
	 * @fires module:cms#cms:action:new:success
	 * @fires module:cms#cms:action:edit:error
	 * @fires module:cms#cms:action:new:error
	 */
	dispatcher.on('cms:action:edit:confirmed', function (form, actionModel) {
		// Vars
		const isNew = actionModel.isNew() || form.type !== actionModel.get('type'),
			// Parse form data and set it on the actionModel:
			link = actionModel.makeLink(form.type, form['link-' + form.type]),
			linkAttributes = {
				route: link.replace(/^#/, ''),
				link: link,
				label: form['link-label'],
				contentLabel: form['content-label'],
				type: form.type,
			};

		if (linkAttributes.type === 'action') {
			linkAttributes.event = link;
		} else if (isNew) {
			// generate new event name for link action
			linkAttributes.event =
				'cms:action:' + actionModel.get('portalId') + ':' + randomChars(10);
		}

		// track old event in order to update menu if successful
		const oldEvent = actionModel.get('event');

		// create a temporary model and
		// only update the UI if successful
		const modelForSaving = new CMSActionModel(
			Object.assign(_.clone(actionModel.attributes), linkAttributes),
		);
		modelForSaving.save().then(
			function (id) {
				if (Number.isInteger(id) && id > 0) {
					// DAM-20542 - set id to allow saving future edits
					actionModel.set('id', id);
				} else {
					logger.warn('no action id returned');
				}
				const menus = Netx.cms.portal.menus;
				// do we need to update any menus?
				// get latest menus (may not have them at all)
				menus.fetchPortal(actionModel.get('portalId')).then(() => {
					// success, update the UI
					actionModel.set(linkAttributes);

					// there's only one menu
					// step through actions and update were necessary
					// fetching wont save us, they are stored separately
					const menu = menus.first();
					const menuActions = menu.get('actions');
					menuActions.forEach((menuItem, idx, all) => {
						// is this a submenu? only ever one level deep
						if (menuItem.actions) {
							const menuIdx = menuItem.actions.indexOf(oldEvent);
							if (menuIdx > -1) {
								// found action, update
								menuItem.actions.splice(menuIdx, 1, actionModel.get('event'));
							}
						} else if (menuItem === oldEvent) {
							// found top level action, update
							menuActions.splice(idx, 1, actionModel.get('event'));
						}
					});
					// set and save
					menu.set('actions', menuActions);
					menu.save().then(() => {
						// dismiss modal and notify
						dispatcher.trigger('cms:action:edit:success', form, actionModel);
					});
				});

				if (isNew) {
					// This is more specific:
					dispatcher.trigger('cms:action:new:success', form, actionModel);
				}
			},
			function (errors) {
				// Error
				dispatcher.trigger('cms:action:edit:error', errors, actionModel);
			},
		);
	});

	/**
	 * @event module:cms#cms:action:edit:success
	 */
	dispatcher.on(
		'cms:action:edit:success',
		function (form, actionModel) {
			var isNew = actionModel.isNew();
			dispatcher.trigger('notification', {
				module: 'cms',
				title: isNew
					? i18n.t('i18n.actionCreated', {defaultValue: 'Link created'})
					: i18n.t('i18n.actionUpdated', {defaultValue: 'Link updated'}),
				description: isNew
					? i18n.t('i18n.theActionWasCreated', {
							defaultValue: 'The link was created.',
					  })
					: i18n.t('i18n.theActionWasUpdated', {
							defaultValue: 'The link was updated',
					  }),
				type: 'success',
			});
		},
		window,
	);

	/**
	 * @event module:cms#cms:action:edit:error
	 */
	dispatcher.on(
		'cms:action:edit:error',
		function (error) {
			dispatcher.trigger(
				'app:alert',
				error,
				i18n.t('i18n.error', {defaultValue: 'Error'}),
			);
		},
		window,
	);

	/**
	 * @event module:cms#cms:action:delete
	 * @fires module:cms#cms:page:delete
	 * @fires module:app#app:confirm:delete
	 */
	dispatcher.on('cms:action:delete', function (action) {
		// DAM-15686 - If deleting a page action, go through page:delete checks
		if (action.get('type') === 'page') {
			const pageId = Netx.cms.portal.pages.getPageIdFromAction(action.toJSON());
			const page = new PageModel({id: pageId});
			$.when(page.fetch()).then((pageData) => {
				dispatcher.trigger('cms:page:delete', page);
			});
		} else {
			dispatcher.trigger('app:confirm:delete', {
				message: i18n.t('i18n.confirmDeleteAction', {
					label: action.get('label'),
				}),
				eventStr: 'cms:action:delete',
				args: [action],
			});
		}
	});

	/**
	 * @event module:cms#cms:action:delete:success
	 */
	dispatcher.on(
		'cms:action:delete:success',
		function (action) {
			dispatcher.trigger('notification', {
				module: 'cms',
				title: i18n.t('i18n.success'),
				description: i18n.t('i18n.theActionWasDeleted'),
				type: 'success',
			});
		},
		window,
	);

	/***************************************
		CMS PAGE EDITING
	***************************************/

	/**
	 * @event module:cms#cms:page:edit
	 * Route to the page editor with an existing CMS page.
	 * (Hands off to System controller.)
	 */
	dispatcher.on('cms:page:edit', function (page, v, e) {
		// Handoff:
		appRouter.navigate(
			'system/portal/' +
				page.get('portalId') +
				'/pages/' +
				(page.isNew() ? 'new' : page.id),
			{trigger: true},
		);
	});

	/**
	 * @event module:cms#cms:page:title:edit:show
	 * @param {object} viewOptions View options
	 */
	dispatcher.on(
		'cms:page:title:edit:show',
		function (viewOptions) {
			import(
				/* webpackChunkName: "view-CMSPageTitleEdit" */ './views/CMSPageTitleEditView'
			).then((View) => {
				modalViews.add({
					viewClass: View.default,
					showActions: ['cms:page:title:edit:show'],
					viewOptions: viewOptions,
				});
			});
		},
		window,
	);
	/**
	 * Add a CMS page to a portal
	 * @event module:cms#cms:page:new
	 * @fires module:cms#cms:page:title:edit:show
	 */
	dispatcher.on('cms:page:new', function (portalModel) {
		const pageModel = new PageModel({
			portalId: portalModel.id,
			userId: userProfile.id,
			title: portalModel.pages.makeUniqueName('i18n.newPage'),
		});
		pageModel.portalModel = portalModel;
		pageModel.newPage = true;

		// Show
		dispatcher.trigger('cms:page:title:edit:show', {
			model: pageModel,
		});
	});

	/**
	 * Edit the title/slug of an existing page (via pageTitleEditView)
	 * @event module:cms#cms:page:title:edit
	 * @fires module:cms#cms:page:title:edit:show
	 */
	dispatcher.on('cms:page:title:edit', function (portalId, page) {
		// dispatcher.trigger('cms:actions:init', portalId);
		// TODO: if page is fetching, delay until it's done?  Can that be a problem or not?
		// Show
		dispatcher.trigger('cms:page:title:edit:show', {
			model: page,
		});
	});

	/**
	 * Handle the submission of pageTitleEditView
	 * @event module:cms#cms:page:title:edit:confirmed
	 * @fires module:cms#cms:page:title:edit:success
	 */
	dispatcher.on('cms:page:title:edit:confirmed', function (form, page) {
		const portalModel = page.portalModel;
		const isNew = page.newPage ? page.newPage : false;

		if (isNew) {
			page.set(_.pick(form, 'title', 'slug', 'displayTitle'));
			page.create().then(
				() => {
					portalModel.pages.add(page);
					portalModel.actions.createActionForPage(page).then((action) => {
						dispatcher.trigger(
							'cms:page:title:edit:success',
							form,
							page,
							isNew,
						);
					});
				},
				(err) => {
					dispatcher.trigger('notification', {
						module: 'cms',
						title: i18n.t('i18n.errorCreatingPage'),
						description: i18n.t('i18n.errorCreatingPage'),
						type: 'danger',
					});
				},
			);
		} else {
			dispatcher.trigger('cms:page:title:edit:success', form, page, isNew);
		}
	});

	/**
	 * When the name of a page changes, we must also change its action
	 * @event module:cms#cms:page:title:edit:success
	 * @fires module:cms#cms:page:template:edit
	 * @fires module:cms#cms:page:save:confirmed
	 */
	dispatcher.on('cms:page:title:edit:success', function (form, page, isNew) {
		if (isNew) {
			// If this is a NEW page, just show the modal to choose a template
			dispatcher.trigger('cms:page:template:edit', page);
		} else {
			const newTitle = form.title;
			const newSlug = form.slug;
			const currentHomeRoute = Netx.cms.portal.get('homeRoute');
			const isCurrentHomePage = currentHomeRoute === page.get('slug');

			// set page, that will update the action
			page.set(_.pick(form, 'title', 'slug', 'displayTitle'));
			dispatcher.trigger('cms:page:save:confirmed', form, page);

			// set home route on the portal model
			// if this page is the home page
			if (isCurrentHomePage) {
				Netx.cms.portal.savePortal({
					homeRoute: newSlug,
				});
			}
		}
	});

	/**
	 * @event module:cms#cms:page:template:edit:show
	 * @param {object} viewOptions View options
	 * @fires moudle:app#app:showOptions
	 */
	dispatcher.on(
		'cms:page:template:edit:show',
		function (viewOptions) {
			dispatcher.trigger('app:showOptions', {
				...viewOptions,
				id: 'portal-template-options',
			});
		},
		window,
	);
	/**
	 * Prompt for a page template (via CMSPageTemplateEditView)
	 * @event module:cms#cms:page:template:edit
	 * @fires module:cms#cms:page:template:edit:show
	 */
	dispatcher.on('cms:page:template:edit', function (page) {
		// TODO: if page is fetching, delay until it's done?  Can that be a problem or not?
		// Show
		dispatcher.trigger('cms:page:template:edit:show', {
			menu: Menus.chooseTemplate,
			data: page,
		});
	});

	/**
	 * Set the chosen template on the page
	 * @event module:cms#cms:page:template:edit:confirmed
	 * @fires module:cms#cms:page:template:edit:success
	 */
	dispatcher.on('cms:page:template:edit:confirmed', function (form, page) {
		page.set(_.pick(form, 'template'));
		dispatcher.trigger('cms:page:template:edit:success', form, page);
	});

	/**
	 * UI handler:
	 * Add a CMS Page to a Portal: step 3 where we show the page after the template is chosen
	 * @event module:cms#cms:page:template:edit:success
	 * @fires module:cms#cms:page:save:confirmed
	 * @fires module:cms#system:portal:page:show
	 */
	dispatcher.on(
		'cms:page:template:edit:success',
		function (form, page) {
			dispatcher.trigger('cms:page:save:confirmed', form, page);

			dispatcher.once('cms:page:save:success', function () {
				dispatcher.trigger('system:portal:page:show');
			});
		},
		window,
	);

	/**
	 * Handle syncing all the changes in an edited page object
	 * @event module:cms#cms:page:save:confirmed
	 * @fires module:cms#cms:page:save:success
	 * @fires module:cms#cms:page:save:error
	 */
	dispatcher.on('cms:page:save:confirmed', function (form, pageModel) {
		pageModel.update().then(
			function (pageId) {
				dispatcher.trigger('cms:page:save:success', pageModel);
			},
			function (err) {
				dispatcher.trigger('cms:page:save:error', err, pageModel);
			},
		);
	});

	/**
	 * UI handler: announce happy page save times
	 * @event module:cms#cms:page:save:success
	 * @fires module:cms#cms:page:edit
	 */
	dispatcher.on(
		'cms:page:save:success',
		function (page) {
			dispatcher.trigger('notification', {
				module: 'cms',
				ids: [page.id],
				title: i18n.t('i18n.pageSaved', {defaultValue: 'Page saved'}),
				description: i18n.t('i18n.pageNameSaved', {
					defaultValue: '"__name__" was saved.',
					name: page.get('title'),
				}),
				type: 'success',
			});
			dispatcher.trigger('cms:page:edit', page);
		},
		window,
	);

	/**
	 * @event module:cms#cms:page:save:error
	 * @param {string} error Error string
	 * @fires module:app#app:alert
	 */
	dispatcher.on('cms:page:save:error', function (error) {
		dispatcher.trigger('app:alert', error, i18n.t('i18n.error'));
	});

	/**
	 * Handle edit cancellation:
	 * restore page to previously sync'ed state.
	 * @event module:cms#cms:page:edit:cancel
	 * @fires module:cms#cms:page:edit:cancelled
	 */
	dispatcher.on('cms:page:edit:cancel', function (page) {
		// restore the page from its server version:
		page.fetch();
		dispatcher.trigger('cms:page:edit:cancelled', page);
	});

	/**
	 * UI handler: route to pages view after cancelling an edit.
	 * (Hand back to System controller.)
	 * @event module:cms#cms:page:edit:cancelled
	 */
	dispatcher.on(
		'cms:page:edit:cancelled',
		function (page) {
			appRouter.navigate(`system/portal/${page.get('portalId')}/pages`, {
				trigger: true,
			});
		},
		window,
	);

	/**
	 * Handle deleting a page
	 * @event module:cms#cms:page:delete
	 * @fires module:cms#cms:page:delete:error
	 * @fires module:app#app:confirm:delete
	 */
	dispatcher.on('cms:page:delete', function (page) {
		// If this page is the home route, it should not be deleted (DAM-15051)
		if (
			Netx.system.portals.get(page.get('portalId')).get('homeRoute') ===
			page.get('slug')
		) {
			dispatcher.trigger(
				'cms:page:delete:error',
				new Error(
					i18n.t('i18n.deletePageHomeRoute', {
						page: page.get('title'),
					}),
				),
				page,
			);
		} else {
			dispatcher.trigger('app:confirm:delete', {
				message: i18n.t('i18n.confirmDeletePage', {
					title: page.get('title'),
				}),
				eventStr: 'cms:page:delete',
				args: [page],
			});
		}
	});

	/**
	 * @event module:cms#cms:page:delete:confirmed
	 * @fires module:cms#cms:action:delete:confirmed
	 */
	dispatcher.on('cms:page:delete:confirmed', function (page) {
		// delete the page by deleting the action

		// if actions have been fetched and added to the page then this is easy
		if (page.action) {
			dispatcher.trigger('cms:action:delete:confirmed', page.action);
			return;
		}

		let actions = Netx.cms.portal.actions;
		// if actions loaded but not added to the page
		// otherwise fetch actions, if it exists start the delete process
		if (actions.length) {
			let action = actions.find({link: `#page/${page.id}`});
			if (action) {
				// action exists, continue
				dispatcher.trigger('cms:action:delete:confirmed', action);
			} else {
				// action does not exist, this is a broken page, delete anyway
				dispatcher.trigger(
					'cms:action:delete:confirmed',
					new CMSActionModel({
						link: `#page/${page.id}`,
						portalId: page.get('portalId'),
						type: 'page',
					}),
				);
			}
		} else {
			const portalId = page.get('portalId');
			if (portalId) {
				actions.fetchPortal(portalId).then((result) => {
					let action = actions.find({link: `#page/${page.id}`});
					if (action) {
						// if action delete is successful then any associated pages or menu items
						// will also be deleted
						dispatcher.trigger('cms:action:delete:confirmed', action);
					} else {
						// action does not exist, this is a broken page, delete anyway
						dispatcher.trigger(
							'cms:action:delete:confirmed',
							new CMSActionModel({
								link: `#page/${page.id}`,
								portalId: portalId,
								type: 'page',
							}),
						);
					}
				});
			}
		}
	});

	/**
	 * Handle error deleting page from portal
	 * @event module:cms#cms:page:delete:error
	 * @fires module:app#app:alert
	 */
	dispatcher.on('cms:page:delete:error', function (error, page) {
		dispatcher.trigger('app:alert', error, 'Error');
	});

	/**
	 * @event module:cms#cms:page:delete:success
	 * @fires module:notification#notification
	 */
	dispatcher.on(
		'cms:page:delete:success',
		function (page) {
			dispatcher.trigger('notification', {
				module: 'cms',
				ids: [page.id],
				title: i18n.t('i18n.pageDeleted'),
				description: i18n.t('i18n.pageDeleted'),
				type: 'success',
			});
		},
		window,
	);
	/***************************************
		END CMS PAGE EDITING
	***************************************/

	/***************************************
		CMS MENU EDITING
	***************************************/

	/**
	 * The CMS Menu editor uses the jquery-sortable plugin to modify a nested set of HTML elements directly;
	 * This form-submit handler takes that element and parses it back into the contents of a Netx.Menu object,
	 * then updates our current menu and syncs it with the server.
	 * @event module:cms#cms:menu:edit:confirmed
	 * @fires module:cms#cms:menu:edit:success
	 * @fires module:cms#cms:menu:edit:error
	 */
	dispatcher.on('cms:menu:edit:confirmed', function (form, menu) {
		menu.set(form.menu);
		menu.save().then(
			function () {
				dispatcher.trigger('cms:menu:edit:success', menu);
			},
			function (err) {
				dispatcher.trigger('cms:menu:edit:error', err, menu);
			},
		);
	});
	/**
	 * @event module:cms#cms:menu:edit:success
	 * @fires module:app#notification
	 */
	dispatcher.on('cms:menu:edit:success', function () {
		dispatcher.trigger('notification', {
			module: 'cms',
			title: i18n.t('i18n.menuSaved'),
			description: i18n.t('i18n.menuSaved'),
			type: 'success',
		});
	});
	/**
	 * @event module:cms#cms:menu:edit:success
	 * @fires module:app#notification
	 */
	dispatcher.on('cms:menu:remove:success', function () {
		dispatcher.trigger('notification', {
			module: 'cms',
			title: i18n.t('i18n.menuRemoved'),
			description: i18n.t('i18n.menuRemoved'),
			type: 'success',
		});
	});
	/**
	 * @event module:cms#cms:menu:edit:error
	 * @fires module:app#app:alert
	 */
	dispatcher.on('cms:menu:edit:error', function (error) {
		dispatcher.trigger(
			'app:alert',
			error,
			i18n.t('i18n.error', {defaultValue: 'Error'}),
		);
	});

	/**
	 * @event module:cms#cms:menu:edit:cancelled
	 */
	dispatcher.on('cms:menu:edit:cancelled', function (menu) {
		menu.fetch(); // restore to server state.
	});

	/***************************************
		END CMS MENU EDITING
	***************************************/

	// END EVENT HANDLERS
	// ==================
	dispatcher.trigger('cms:extend', NS);
	dispatcher.trigger('cms:initialized', NS);
});

export default {
	initialize(opts) {
		return new Promise((resolve, reject) => {
			dispatcher.once('cms:initialized', () => {
				resolve(routes);
			});
			dispatcher.trigger('cms:init');
		});
	},
};
